/**
 * HorizonControl — the planning-window control: a segmented picker (Woche /
 * 2 Wochen / Monat) plus date-range nav that steps by the selected horizon.
 * Feeds the board's `days` and the shift read's `{from, to}`. No hardcoded 7.
 */
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { SegmentedControl } from '@/components/ui/segmented-control'
import { formatRange, isHorizon, type Horizon } from './rosterModel'
import type { RosterHorizon } from './useRosterHorizon'

// Label a horizon by its day count: the common calendar windows get a name,
// anything else (a ward's custom cadence from config) reads as "N Tage".
function horizonLabel(h: Horizon, t: ReturnType<typeof useTranslation>['t']): string {
  if (h === 7) return t('roster.horizon.week', 'Week')
  if (h === 14) return t('roster.horizon.twoWeeks', '2 weeks')
  if (h === 28 || h === 30 || h === 31) return t('roster.horizon.month', 'Month')
  return t('roster.horizon.days', '{{count}} days', { count: h })
}

export function HorizonControl({
  horizon,
  locale,
}: {
  horizon: RosterHorizon
  locale: string
}) {
  const { t } = useTranslation()

  const options = horizon.horizons.map((h) => ({
    id: String(h),
    label: horizonLabel(h, t),
  }))

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        value={String(horizon.horizon)}
        options={options}
        onChange={(id) => {
          const n = Number(id)
          if (isHorizon(n, horizon.horizons)) horizon.setHorizon(n)
        }}
        className="bg-[var(--color-neutral-100)]"
      />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={horizon.prev}
          aria-label={t('roster.horizon.prev', 'Previous period')}
          className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-surface-mute)] transition active:scale-95 hover:bg-[var(--color-neutral-100)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="min-w-[7.5rem] text-center text-[14px] font-semibold tabular-nums text-[var(--color-surface-ink)]">
          {formatRange(horizon.from, horizon.to, locale)}
        </span>
        <button
          type="button"
          onClick={horizon.next}
          aria-label={t('roster.horizon.next', 'Next period')}
          className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-surface-mute)] transition active:scale-95 hover:bg-[var(--color-neutral-100)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
